import React, { useState, useEffect, useRef } from "react";

import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Box from "@material-ui/core/Box";

import { fetchGetRequest, fetchRequest } from "common/service";
import GridContainer from "components/Grid/GridContainer.js";
import Button from "components/CustomButtons/Button.js";
import GridItem from "components/Grid/GridItem.js";
import { API_BASE_URL } from "common/constants";

const useStyles = makeStyles(() => ({
  filterContainer: { backgroundColor: "#ffffff", borderRadius: "5px", padding: "16px", marginBottom: "16px" },
  filterLabel: { marginBottom: "16px", color: "#333333", fontWeight: 600 },
  textField: {"& .MuiInputLabel-root": { fontSize: "14px" } },
  applyButton: { marginTop: "8px", borderRadius: "4px" },
}));

const NewFiltersBox = ({ onApply, type = "", friendlyNameHelperTextLabel = "", setCompanyList = () => {}, setUsersList = () => {} }) => {
    const userDetails = JSON.parse(localStorage.getItem("user-details"));
    const loggedUserRole = userDetails !== null ? userDetails.roles[0].role : "";
    
    const [selectedCompany, setSelectedCompany] = useState("cd5dc314-6e25-48a8-9d47-9e574b8fe3a0");
    const [selectedFriendlyName, setSelectedFriendlyName] = useState("all");
    const [selectedUserName, setSelectedUserName] = useState("Select All");
    const [selectedFacility, setSelectedFacility] = useState("all");
    const [allFacilitiesList, setAllFacilitiesList] = useState({});
    const [friendlyNameList, setFriendlyNameList] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState("");
    const [selectedUser, setSelectedUser] = useState("all");
    const [companyList, setCompaniesList] = useState([]);
    const [deviceList, setDeviceList] = useState([]);
    const [userList, setUserList] = useState([]);
    const classes = useStyles();

    const handleApply = () => {
      const filterData = {
        deviceName: deviceList.find((device) => device.deviceId === selectedDevice)?.name.split("-")[0] || undefined,
        emailAddress: userList.find((user) => user.userId === selectedUser)?.emailAddress || undefined,
        friendlyName: selectedFriendlyName || undefined,
        selectedDevice: selectedDevice || undefined,
        facility: selectedFacility || undefined,
        company: selectedCompany || undefined,
        fullName: selectedUserName,
        user: [selectedUser] || [],
        noSensorExist: false
      };

      if(!friendlyNameList?.length) {
        filterData.noSensorExist = true;
      }

      if(type === "dashboard" ) {
        filterData.user = filterData.user === "all" ? "all" : selectedUser;
        filterData.friendlyName = filterData.friendlyName === "all" ? "" : selectedFriendlyName;
      } else {
        if(selectedUser === "all") {
          const _userList = userList.map((user) => user.userId);
          filterData.user = _userList;
        }
      }
      if (onApply) {
        onApply(filterData);
      }
    };

    useEffect(() => {
      getDeviceList();
      getCompanyList();
      if(!["TenantAdmin", "TenantUser"].includes(loggedUserRole)){
        setSelectedCompany(userDetails.roles[0]?.supplier?.supplierId);
      }
    }, []);

    const getDeviceList = async() => {
      const endPoint = `${API_BASE_URL}/user/device?status=Active`;
      const list = await fetchGetRequest("GET", endPoint);
      if(list?.content?.length > 0) {
          const _deviceList = list?.content?.filter((device) => device.name.split("-")[1].toLowerCase() === "pvital");
          setDeviceList(_deviceList);
          setSelectedDevice(_deviceList[0]?.deviceId);
      }
    }

    const getCompanyList = async() => {
      const endPoint = `${API_BASE_URL}/user/supplier`;
      const list = await fetchGetRequest("GET", endPoint);
      setCompaniesList(list?.content || []);
      if(setCompanyList) {
        setCompanyList(list?.content || []);
      }
    }

    const getAllFacilitiesList = async() => {
      let endPoint = `${API_BASE_URL}/user/practice?pageNumber=0&sortBy=name&sortOrder=ASC`;
      if(selectedCompany) {
        endPoint += `&supplierId=${selectedCompany}`;
      }
      const facilityListData = await fetchGetRequest("GET", endPoint);
      setAllFacilitiesList(facilityListData || {});
    }

    const getUsersList = async() => {
        let endPoint = `${API_BASE_URL}/user/user?role=Patient`;
        if(selectedCompany) {
          endPoint += `&supplierId=${selectedCompany}`;
        }
        if(selectedFacility && selectedFacility !== "all") {
            endPoint += `&practiceId=${selectedFacility}`;
        }
        const list = await fetchGetRequest("GET", endPoint);
        setUserList(list?.content || []);
        
        if(loggedUserRole === "Patient") {
          setSelectedUser(userDetails.userId);
          setSelectedUserName(userDetails.fullName);
          getFriendlyNameList(userDetails.userId);
        }

        if(setUsersList) {
          setUsersList(list?.content || []);
        }
    }

    const getFriendlyNameList = async(userId) => {
        const endPoint = `${API_BASE_URL}/data/devicedata-advanced/owner?sortBy=maxValueProvidedOn&sortOrder=DESC`;
        const data = {
            "deviceDataModelId":"d36a4373-fbdc-44a3-8c96-4bb920041e40-id",
            "devicePropertyCodes":["Devid","FriendlyName","Timezone"],
            "ownerFilter":{"users":[userId || selectedUser]}
        }
        const list = await fetchRequest("POST", endPoint, data);
        setFriendlyNameList(list?.content || []);
    }

    const debounceTimerRef = useRef(null);
    useEffect(() => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        getUsersList();
      }, 500);
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, [selectedCompany, selectedFacility]);

    const debounceTimerRef2 = useRef(null);
    useEffect(() => {
      if (debounceTimerRef2.current) {
        clearTimeout(debounceTimerRef2.current);
      }
      debounceTimerRef2.current = setTimeout(() => {
        if(["TenantAdmin", "TenantUser", "SupplierAdmin", "SupplierUser", "PracticeAdmin", "PracticeUser"].includes(loggedUserRole)) {
          getAllFacilitiesList();
        } else {
          setSelectedFacility(userDetails.roles[0]?.practice?.practiceId);
        }
      }, 500);
      return () => {
        if (debounceTimerRef2.current) {
          clearTimeout(debounceTimerRef2.current);
        }
      };
    }, [selectedCompany]);

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Box className={classes.filterContainer}>
          <GridContainer>
            {["TenantAdmin", "TenantUser"].includes(loggedUserRole) && <GridItem xs={12} sm={12} md={2}>
              <TextField
                fullWidth
                select
                label="Device"
                value={selectedDevice}
                onChange={(e) => setSelectedDevice(e.target.value)}
                margin="normal"
                variant="standard"
                className={classes.textField}
              >
                {deviceList.map((deviceItem) => (
                  <MenuItem key={deviceItem.deviceId} value={deviceItem.deviceId}>
                    {deviceItem.name.split("-")[0]}
                  </MenuItem>
                ))}
              </TextField>
            </GridItem>}
            
            {["TenantAdmin", "TenantUser"].includes(loggedUserRole) && <GridItem xs={12} sm={12} md={2}>
              <TextField
                fullWidth
                select
                label="Company"
                value={selectedCompany}
                onChange={(e) => {
                    setSelectedCompany(e.target.value);
                    setSelectedFacility("all");
                }}
                margin="normal"
                variant="standard"
                className={classes.textField}
              >
                {companyList.map((companyItem) => (
                  <MenuItem key={companyItem.supplierId} value={companyItem.supplierId}>
                    {companyItem.name}
                  </MenuItem>
                ))}
              </TextField>
            </GridItem>}

            {["TenantAdmin", "TenantUser", "SupplierAdmin", "SupplierUser"].includes(loggedUserRole) && <GridItem xs={12} sm={12} md={2}>
              <TextField
                fullWidth
                select
                label="Facility"
                value={selectedFacility}
                onChange={(e) => setSelectedFacility(e.target.value)}
                margin="normal"
                variant="standard"
                className={classes.textField}
              >
                <MenuItem value="all">Select All {allFacilitiesList?.content?.length > 0 ? `(${allFacilitiesList?.content?.length})` : ""}</MenuItem>
                {selectedCompany ? allFacilitiesList?.content?.filter((facilityItem) => facilityItem.supplierId === selectedCompany).map((facilityItem) => (
                    <MenuItem key={facilityItem.practiceId} value={facilityItem.practiceId}>
                      {facilityItem.name}
                    </MenuItem>
                  )) : allFacilitiesList?.content?.map((facilityItem) => (
                    <MenuItem key={facilityItem.practiceId} value={facilityItem.practiceId}>
                      {facilityItem.name}
                    </MenuItem>
                  ))}
              </TextField>
            </GridItem>}

            {["TenantAdmin", "TenantUser", "SupplierAdmin", "SupplierUser", "PracticeAdmin", "PracticeUser"].includes(loggedUserRole) && <GridItem xs={12} sm={12} md={3}>
              <Autocomplete
                disabled={!userList?.length}
                options={[{userId: "all", fullName: `Select All`}, ...userList.sort((a, b) => a.firstName > b.firstName ? 1 : -1)]}
                value={selectedUser === "all" ? {userId: "all", fullName: `Select All ${userList?.length > 0 ? `(${userList?.length})` : "(0)"}`} : userList.find((user) => user.userId === selectedUser) || null}
                renderInput={(params) => (<TextField {...params}  label="User"  margin="normal" variant="standard" className={classes.textField} />)}
                getOptionLabel={(option) => option?.fullName}
                disableClearable={true}
                filterSelectedOptions
                onChange={(eventChange, val) => {
                    setSelectedUser(val?.userId || "all");
                    setSelectedUserName(val?.fullName || "Select All");
                    setFriendlyNameList([]);
                    if (val?.userId !== "all") {
                      getFriendlyNameList(val.userId);
                    }
                }}
              />
            </GridItem>}

            <GridItem xs={12} sm={12} md={3}>
              <Autocomplete
                disabled={selectedUser === "all" || friendlyNameList?.length === 0}
                options={[
                  {devid: "all", friendlyName: "Select All"},
                  ...friendlyNameList.map((item) => ({
                    devid: item?.data?.Devid?.value,
                    friendlyName: item?.data?.FriendlyName?.value
                  }))
                ]}
                onChange={(eventChange, val) => {setSelectedFriendlyName(val?.devid || "all")}}
                renderInput={(params) => (<TextField {...params} helperText={friendlyNameHelperTextLabel} label="Friendly Name" margin="normal" variant="standard" className={classes.textField} />)}
                getOptionLabel={(option) => option?.friendlyName}
                disableClearable={true}
                filterSelectedOptions
                value={selectedFriendlyName === "all" ? {devid: "all", friendlyName: `Select All ${friendlyNameList?.length > 0 ? `(${friendlyNameList?.length})` : "(0)"}`} : friendlyNameList
                    .map((item) => ({devid: item?.data?.Devid?.value, friendlyName: item?.data?.FriendlyName?.value}))
                    .find((item) => item.devid === selectedFriendlyName) || null
                }
              />
            </GridItem>

            <GridItem xs={12} sm={12} md={2}>
              <Box display="flex" alignItems="flex-end" height="100%" paddingTop={1}>
                <Button disabled={!userList?.length} color="danger" onClick={handleApply} className={classes.applyButton}>Fetch Data</Button>
                {/* <Button color="danger" onClick={onReset} className={classes.applyButton}>RESET</Button> */}
              </Box>
            </GridItem>
          </GridContainer>
        </Box>
      </GridItem>
    </GridContainer>
  );
};

export default NewFiltersBox;
